import { useState, useEffect, useCallback } from "react";
import {
  Upload,
  FileText,
  Plus,
  Trash2,
  CheckCircle,
  Clock,
  AlertCircle,
  X,
} from "lucide-react";
import { invoiceApi, warehouseApi } from "../lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";

export default function Invoices() {
  const [invoices, setInvoices] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [manualDialogOpen, setManualDialogOpen] = useState(false);
  const [processDialogOpen, setProcessDialogOpen] = useState(false);
  const [extractedData, setExtractedData] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [selectedWarehouse, setSelectedWarehouse] = useState("");
  const [dragging, setDragging] = useState(false);

  const [manualForm, setManualForm] = useState({
    invoice_number: "",
    invoice_type: "purchase",
    supplier_customer: "",
    warehouse_id: "",
    items: [{ sku: "", name: "", quantity: "", unit_price: "" }],
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [invoicesRes, warehousesRes] = await Promise.all([
        invoiceApi.getAll(),
        warehouseApi.getAll(),
      ]);
      setInvoices(invoicesRes.data);
      setWarehouses(warehousesRes.data);
      if (warehousesRes.data.length > 0) {
        setManualForm((prev) => ({
          ...prev,
          warehouse_id: warehousesRes.data[0].id,
        }));
        setSelectedWarehouse(warehousesRes.data[0].id);
      }
    } catch (error) {
      toast.error("Failed to load invoices");
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (file) => {
    if (!file) return;
    
    const validTypes = ["image/jpeg", "image/png", "image/webp", "application/pdf"];
    if (!validTypes.includes(file.type)) {
      toast.error("Please upload a JPG, PNG, WEBP, or PDF file");
      return;
    }

    setUploading(true);
    try {
      const response = await invoiceApi.upload(file);
      if (response.data.success) {
        setExtractedData(response.data.extracted_data);
        toast.success("Invoice data extracted successfully");
      } else {
        toast.error("Could not extract data from invoice");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to process invoice");
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    handleFileUpload(file);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
  }, []);

  const saveExtractedInvoice = async () => {
    if (!extractedData || !selectedWarehouse) {
      toast.error("Please select a warehouse");
      return;
    }

    try {
      const invoiceData = {
        invoice_number: extractedData.invoice_number || `INV-${Date.now()}`,
        invoice_type: extractedData.invoice_type || "purchase",
        supplier_customer: extractedData.supplier_customer || "",
        warehouse_id: selectedWarehouse,
        items: extractedData.items || [],
      };

      await invoiceApi.createManual(invoiceData);
      toast.success("Invoice saved");
      setExtractedData(null);
      fetchData();
    } catch (error) {
      toast.error("Failed to save invoice");
    }
  };

  const handleManualSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...manualForm,
        items: manualForm.items.map((item) => ({
          ...item,
          quantity: parseInt(item.quantity),
          unit_price: parseFloat(item.unit_price),
        })),
      };
      await invoiceApi.createManual(data);
      toast.success("Invoice created");
      setManualDialogOpen(false);
      resetManualForm();
      fetchData();
    } catch (error) {
      toast.error("Failed to create invoice");
    }
  };

  const handleProcessInvoice = async () => {
    if (!selectedInvoice || !selectedWarehouse) return;
    try {
      await invoiceApi.process(selectedInvoice.id, selectedWarehouse);
      toast.success("Invoice processed - inventory updated");
      setProcessDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to process invoice");
    }
  };

  const handleDeleteInvoice = async (id) => {
    if (!window.confirm("Delete this invoice?")) return;
    try {
      await invoiceApi.delete(id);
      toast.success("Invoice deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete invoice");
    }
  };

  const resetManualForm = () => {
    setManualForm({
      invoice_number: "",
      invoice_type: "purchase",
      supplier_customer: "",
      warehouse_id: warehouses[0]?.id || "",
      items: [{ sku: "", name: "", quantity: "", unit_price: "" }],
    });
  };

  const addManualItem = () => {
    setManualForm({
      ...manualForm,
      items: [...manualForm.items, { sku: "", name: "", quantity: "", unit_price: "" }],
    });
  };

  const removeManualItem = (index) => {
    if (manualForm.items.length === 1) return;
    setManualForm({
      ...manualForm,
      items: manualForm.items.filter((_, i) => i !== index),
    });
  };

  const updateManualItem = (index, field, value) => {
    const newItems = [...manualForm.items];
    newItems[index][field] = value;
    setManualForm({ ...manualForm, items: newItems });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="animate-fadeIn" data-testid="invoices-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Invoices</h1>
          <p className="text-slate-400">Upload invoices or add manually</p>
        </div>
        <Button
          onClick={() => {
            resetManualForm();
            setManualDialogOpen(true);
          }}
          className="bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-sm flex items-center gap-2 text-sm btn-transition glow-blue"
          data-testid="manual-invoice-btn"
        >
          <Plus className="w-4 h-4" />
          Manual Entry
        </Button>
      </div>

      <Tabs defaultValue="upload" className="mb-8">
        <TabsList className="bg-[#151923] border border-slate-800">
          <TabsTrigger value="upload" className="data-[state=active]:bg-blue-600">
            AI Upload
          </TabsTrigger>
          <TabsTrigger value="history" className="data-[state=active]:bg-blue-600">
            Invoice History
          </TabsTrigger>
        </TabsList>

        <TabsContent value="upload" className="mt-6">
          {/* Upload Zone */}
          <div
            className={`upload-zone ${dragging ? "dragging" : ""}`}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            data-testid="upload-zone"
          >
            {uploading ? (
              <div className="flex flex-col items-center">
                <div className="spinner mb-4" />
                <p className="text-slate-400">Extracting invoice data...</p>
              </div>
            ) : (
              <>
                <Upload className="w-12 h-12 text-slate-500 mx-auto mb-4" />
                <p className="text-slate-300 mb-2">Drop invoice image here or click to upload</p>
                <p className="text-sm text-slate-500">Supports JPG, PNG, WEBP, PDF</p>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp,application/pdf"
                  className="absolute inset-0 opacity-0 cursor-pointer"
                  onChange={(e) => handleFileUpload(e.target.files[0])}
                  data-testid="file-input"
                />
              </>
            )}
          </div>

          {/* Extracted Data Preview */}
          {extractedData && (
            <div className="mt-6 bg-[#151923] border border-slate-800 rounded-sm p-6" data-testid="extracted-data">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white">Extracted Invoice Data</h3>
                <button
                  onClick={() => setExtractedData(null)}
                  className="text-slate-400 hover:text-white"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div>
                  <p className="text-xs text-slate-500 uppercase mb-1">Invoice Number</p>
                  <p className="text-slate-200 font-mono">{extractedData.invoice_number || "N/A"}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase mb-1">Type</p>
                  <span className={`badge ${extractedData.invoice_type === "purchase" ? "badge-blue" : "badge-success"}`}>
                    {extractedData.invoice_type || "purchase"}
                  </span>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase mb-1">Supplier/Customer</p>
                  <p className="text-slate-200">{extractedData.supplier_customer || "N/A"}</p>
                </div>
              </div>

              {extractedData.items?.length > 0 && (
                <div className="mb-4">
                  <p className="text-xs text-slate-500 uppercase mb-2">Items</p>
                  <div className="overflow-x-auto">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>SKU</th>
                          <th>Name</th>
                          <th>Qty</th>
                          <th>Unit Price</th>
                          <th>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {extractedData.items.map((item, i) => (
                          <tr key={i}>
                            <td className="text-blue-400">{item.sku}</td>
                            <td>{item.name}</td>
                            <td>{item.quantity}</td>
                            <td>${item.unit_price?.toFixed(2)}</td>
                            <td>${(item.quantity * item.unit_price).toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <div className="flex items-center gap-4">
                <Select value={selectedWarehouse} onValueChange={setSelectedWarehouse}>
                  <SelectTrigger className="w-48 bg-[#0B0E14] border-slate-700" data-testid="warehouse-select">
                    <SelectValue placeholder="Select warehouse" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#151923] border-slate-700">
                    {warehouses.map((w) => (
                      <SelectItem key={w.id} value={w.id}>
                        {w.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  onClick={saveExtractedInvoice}
                  className="bg-blue-600 hover:bg-blue-500"
                  data-testid="save-extracted-btn"
                >
                  Save Invoice
                </Button>
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="history" className="mt-6">
          {invoices.length > 0 ? (
            <div className="bg-[#151923] border border-slate-800 rounded-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Invoice #</th>
                      <th>Type</th>
                      <th>Supplier/Customer</th>
                      <th>Items</th>
                      <th>Total</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.map((invoice) => (
                      <tr key={invoice.id} className="table-row-hover" data-testid={`invoice-row-${invoice.invoice_number}`}>
                        <td className="font-mono text-blue-400">{invoice.invoice_number}</td>
                        <td>
                          <span className={`badge ${invoice.invoice_type === "purchase" ? "badge-blue" : "badge-success"}`}>
                            {invoice.invoice_type}
                          </span>
                        </td>
                        <td>{invoice.supplier_customer || "-"}</td>
                        <td>{invoice.items?.length || 0}</td>
                        <td>${invoice.total_amount?.toFixed(2) || "0.00"}</td>
                        <td>
                          {invoice.status === "processed" ? (
                            <span className="badge badge-success flex items-center gap-1">
                              <CheckCircle className="w-3 h-3" /> Processed
                            </span>
                          ) : (
                            <span className="badge badge-warning flex items-center gap-1">
                              <Clock className="w-3 h-3" /> Pending
                            </span>
                          )}
                        </td>
                        <td>
                          <div className="flex items-center gap-2">
                            {invoice.status === "pending" && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => {
                                  setSelectedInvoice(invoice);
                                  setProcessDialogOpen(true);
                                }}
                                className="text-blue-400 hover:text-blue-300"
                                data-testid={`process-invoice-${invoice.invoice_number}`}
                              >
                                Process
                              </Button>
                            )}
                            <button
                              onClick={() => handleDeleteInvoice(invoice.id)}
                              className="p-1.5 text-slate-400 hover:text-red-400 hover:bg-slate-800 rounded"
                              data-testid={`delete-invoice-${invoice.invoice_number}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="bg-[#151923] border border-slate-800 rounded-sm">
              <div className="empty-state">
                <FileText className="empty-state-icon" />
                <p className="text-slate-400">No invoices yet</p>
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Manual Entry Dialog */}
      <Dialog open={manualDialogOpen} onOpenChange={setManualDialogOpen}>
        <DialogContent className="bg-[#151923] border-slate-700 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Manual Invoice</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleManualSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="form-label">Invoice Number</label>
                <Input
                  value={manualForm.invoice_number}
                  onChange={(e) => setManualForm({ ...manualForm, invoice_number: e.target.value })}
                  className="bg-[#0B0E14] border-slate-700"
                  placeholder="INV-001"
                  required
                  data-testid="manual-invoice-number"
                />
              </div>
              <div>
                <label className="form-label">Type</label>
                <Select
                  value={manualForm.invoice_type}
                  onValueChange={(value) => setManualForm({ ...manualForm, invoice_type: value })}
                >
                  <SelectTrigger className="bg-[#0B0E14] border-slate-700" data-testid="manual-invoice-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#151923] border-slate-700">
                    <SelectItem value="purchase">Purchase (Inventory In)</SelectItem>
                    <SelectItem value="sale">Sale (Inventory Out)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="form-label">Supplier / Customer</label>
                <Input
                  value={manualForm.supplier_customer}
                  onChange={(e) => setManualForm({ ...manualForm, supplier_customer: e.target.value })}
                  className="bg-[#0B0E14] border-slate-700"
                  placeholder="Company name"
                  data-testid="manual-supplier"
                />
              </div>
              <div>
                <label className="form-label">Warehouse</label>
                <Select
                  value={manualForm.warehouse_id}
                  onValueChange={(value) => setManualForm({ ...manualForm, warehouse_id: value })}
                >
                  <SelectTrigger className="bg-[#0B0E14] border-slate-700" data-testid="manual-warehouse">
                    <SelectValue placeholder="Select warehouse" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#151923] border-slate-700">
                    {warehouses.map((w) => (
                      <SelectItem key={w.id} value={w.id}>
                        {w.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="form-label mb-0">Invoice Items</label>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={addManualItem}
                  className="text-blue-400"
                  data-testid="add-item-btn"
                >
                  <Plus className="w-4 h-4 mr-1" /> Add Item
                </Button>
              </div>
              <div className="space-y-3">
                {manualForm.items.map((item, index) => (
                  <div key={index} className="flex gap-2 items-start">
                    <Input
                      value={item.sku}
                      onChange={(e) => updateManualItem(index, "sku", e.target.value.toUpperCase())}
                      className="bg-[#0B0E14] border-slate-700 w-24"
                      placeholder="SKU"
                      required
                      data-testid={`item-sku-${index}`}
                    />
                    <Input
                      value={item.name}
                      onChange={(e) => updateManualItem(index, "name", e.target.value)}
                      className="bg-[#0B0E14] border-slate-700 flex-1"
                      placeholder="Product name"
                      required
                      data-testid={`item-name-${index}`}
                    />
                    <Input
                      type="number"
                      value={item.quantity}
                      onChange={(e) => updateManualItem(index, "quantity", e.target.value)}
                      className="bg-[#0B0E14] border-slate-700 w-20"
                      placeholder="Qty"
                      required
                      data-testid={`item-qty-${index}`}
                    />
                    <Input
                      type="number"
                      step="0.01"
                      value={item.unit_price}
                      onChange={(e) => updateManualItem(index, "unit_price", e.target.value)}
                      className="bg-[#0B0E14] border-slate-700 w-24"
                      placeholder="Price"
                      required
                      data-testid={`item-price-${index}`}
                    />
                    <button
                      type="button"
                      onClick={() => removeManualItem(index)}
                      className="p-2 text-slate-400 hover:text-red-400"
                      disabled={manualForm.items.length === 1}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setManualDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-blue-600 hover:bg-blue-500"
                data-testid="submit-manual-invoice"
              >
                Create Invoice
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Process Invoice Dialog */}
      <Dialog open={processDialogOpen} onOpenChange={setProcessDialogOpen}>
        <DialogContent className="bg-[#151923] border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle>Process Invoice</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-start gap-3 p-4 bg-amber-500/10 border border-amber-500/20 rounded-sm">
              <AlertCircle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-amber-200">
                  Processing this invoice will update inventory levels.
                </p>
                <p className="text-xs text-amber-400/70 mt-1">
                  {selectedInvoice?.invoice_type === "purchase" ? "Products will be added to stock" : "Products will be removed from stock"}
                </p>
              </div>
            </div>

            <div>
              <label className="form-label">Target Warehouse</label>
              <Select value={selectedWarehouse} onValueChange={setSelectedWarehouse}>
                <SelectTrigger className="bg-[#0B0E14] border-slate-700" data-testid="process-warehouse">
                  <SelectValue placeholder="Select warehouse" />
                </SelectTrigger>
                <SelectContent className="bg-[#151923] border-slate-700">
                  {warehouses.map((w) => (
                    <SelectItem key={w.id} value={w.id}>
                      {w.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button
                variant="ghost"
                onClick={() => setProcessDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleProcessInvoice}
                className="bg-blue-600 hover:bg-blue-500"
                data-testid="confirm-process-btn"
              >
                Process Invoice
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
